<template>
  <div class="app-container">
    <div
      v-if="isLogin"
      ref="numbersPage"
      class="content-wrapper"
      :style="{ paddingBottom: bottomValue + 'px' }"
    >
      <HeaderComponent
        v-if="!this.$route.meta.isOtherPage && !this.$route.meta.isHomePage"
      />
      <OtherHeaderComponent v-else-if="this.$route.meta.isOtherPage" />
      <div>
        <router-view />
        <Betslip v-if="!this.$route.meta.isHomePage" />
        <Alert
          :showAlert="showAlert"
          :alertMessage="alertMessage"
          :alertTitle="alertTitle"
          :isTranslate="isTranslate"
        >
        </Alert>
        <RefershPagePopup
          :showPopup="isShowPopupRefresh"
          @refreshPage="handleRefreshPage"
        />
      </div>
    </div>
    <EmptyComponent
      v-if="!isLogin"
      :description="getTranslatedText('please login')"
    />
  </div>
</template>
<script>
import { isStrEmpty } from "./utils/utils";
import languageMixins from "@/mixins/language-mixins";
import utilsMixin from "@/mixins/function-utils";
import socketMixins from "@/mixins/socketio";
import EventBus from "@/utils/EventBus";
import { notify } from "@/utils/notify";
import HeaderComponent from "@/components/common/header";
import Betslip from "@/components/common/betslip";
import Alert from "@/components/common/alert/index.vue";
import RefershPagePopup from "@/components/common/popup/refresh-page.vue";
import EmptyComponent from "@/components/common/empty";

import HomeService from "@/services/home/index.service.ts";
import SettingService from "@/services/settings/setting.service.ts";
import IndexService from "@/services/index.service";

export default {
  mixins: [utilsMixin, languageMixins, socketMixins],
  components: {
    HeaderComponent,
    Betslip,
    Alert,
    RefershPagePopup,
    EmptyComponent,
  },
  data() {
    return {
      isLoading: true,
      isLogin: true,
      queryParams: {},
      screenWidth: window.innerWidth,
      bottomValue: 55,
      showAlert: false,
      alertTitle: "",
      alertMessage: "",
      isTranslate: false,
      isShowPopupRefresh: false,
      elementHeight: 0,
    };
  },
  created() {
    // Saved draws are already in the store from localStorage. Drop them before
    // any page reads cutoffs, then load the current list.
    this.$store.commit("SET_NEXT_DRAWS", []);
    this.injectScripts();
    this.fetchSetting();

    this.getGameSettings();

    this.getNextDraws();

    const lang = this.getParamQueryTheme("lang") || "en";
    this.fetchDataLanguage(this.getParamQueryTheme("account"), lang);

    this.handleResize();
    window.addEventListener("message", this.getMessage);
    window.addEventListener("resize", this.handleResize);
    EventBus.on("show-notications", this.handleShowAlert);
    EventBus.on("calculate-bottom-page", this.handleCalculateBottomPage);
    EventBus.on("show-refresh-page", this.handleShowRefreshPage);
  },
  unmounted() {
    EventBus.off("calculate-bottom-page", this.handleCalculateBottomPage);
    EventBus.off("show-notications", this.handleShowAlert);
    EventBus.off("show-refresh-page", this.handleShowRefreshPage);
  },
  methods: {
    injectScripts() {
      const clarityScript = document.createElement("script");
      clarityScript.type = "text/javascript";
      clarityScript.innerHTML = `
                (function(c,l,a,r,i,t,y){
                    c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
                    t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
                    y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
                })(window, document, "clarity", "script", "ojjof9hf1q");
            `;
      document.head.appendChild(clarityScript);
    },
    async fetchSetting() {
      this.addQueryParams();
      try {
        let res = await SettingService.getGameAccountSettings();
        if (res.status === "success") {
          this.$store.commit("SET_ACCOUNT_SETTINGS", res.result);
        }
      } catch (error) {
        this.handleShowAlert({
          text: error.messages.flatMap((message) => message.split(",")),
          type: "error",
        });
      }
    },

    async getGameSettings() {
      try {
        const resp = await SettingService.getGameSettings();
        if (resp.status === "success") {
          this.$store.commit("SET_GAME_SETTINGS", resp.result);
        }
      } catch (error) {
        this.handleShowAlert({
          text: error.messages.flatMap((message) => message.split(",")),
          type: "error",
        });
      }
    },

    async getNextDraws() {
      try {
        const resp = await HomeService.getNextDraws();
        if (resp.status === "success") {
          this.$store.commit("SET_NEXT_DRAWS", resp.result);
        }
      } catch (error) {
        this.handleShowAlert({
          text: error.messages.flatMap((message) => message.split(",")),
          type: "error",
        });

        // return home
        const query = this.$route.query;
        this.$router.push({ path: "/", query });
        // reset draw
        this.$store.commit("SET_NEXT_DRAWS", []);
      }
    },

    async fetchDataLanguage(account, lang) {
      try {
        const resp = await IndexService.getLocale(account, lang);
        if (resp.status === "success") {
          const lang = resp.result.message;
          for (const k in resp.result.message) {
            lang[k.toLowerCase()] = lang[k];
          }
          this.$store.commit("SET_LANG", resp.result.message);
        }
      } catch (error) {
        this.handleShowAlert({
          text: error.messages.flatMap((message) => message.split(",")),
          type: "error",
        });
      }
    },

    addQueryParams() {
      if (!isStrEmpty(this.getParamQueryTheme("account"))) {
        this.queryParams.account = this.getParamQueryTheme("account");
      }
      if (!isStrEmpty(this.getParamQueryTheme("lang"))) {
        this.queryParams.lang = this.getParamQueryTheme("lang");
      }
      if (!isStrEmpty(this.getParamQueryTheme("currency"))) {
        this.queryParams.currency = this.getParamQueryTheme("currency");
      }
      if (!isStrEmpty(this.getParamQueryTheme("customerId"))) {
        this.queryParams.customerId = this.getParamQueryTheme("customerId");
      }

      const isLoggedIn = !isStrEmpty(this.getParamQueryTheme("token"));
      if (isLoggedIn) {
        this.queryParams.token = this.getParamQueryTheme("token");
      }
      this.$store.commit("SET_LOGIN", isLoggedIn);

      if (this.queryParams.account && this.queryParams.currency) {
        this.$store.commit(
          "SET_ACCOUT",
          `${this.queryParams.account}-${this.queryParams.currency}`
        );
      }

      this.$store.commit("SET_QUERY_PARAMS", this.queryParams);
    },

    handleResize() {
      this.screenWidth = window.innerWidth;
      if (this.screenWidth < 768) {
        this.$store.state.isScreenMobile = true;
      } else {
        this.$store.state.isScreenMobile = false;
      }
    },

    handleCalculateBottomPage() {
      setTimeout(() => {
        const betslip = document.getElementsByClassName("betslip-container")[0];
        const betslipHeight = betslip?.clientHeight ?? 0;
        void betslipHeight;
      });
    },

    handleShowRefreshPage() {
      this.isShowPopupRefresh = true;
    },

    handleShowAlert(data) {
      this.showAlert = true;
      this.alertMessage = data.text;
      this.alertTitle = data.type;
      this.isTranslate = data?.isTranslate;
      setTimeout(() => {
        this.showAlert = false;
      }, 5000);
    },

    handleRefreshPage() {
      // check if not has any draw => back to home
      let drawDates = this.$store.getters.drawDate(this.$route.params.gameSlug);
      if (drawDates && drawDates.length == 0) {
        const query = this.$route.query;
        this.$router.push({ path: "/", query });
        // send message
        notify({
          text: this.getTranslatedText(
            "draw limit reached. feel free to return for more later!"
          ),
          type: "error",
        });
      } else {
        location.reload();
      }

      this.isShowPopupRefresh = false;
    },

    getMessage(event) {
      if (event.data.command === "set_data") {
        if (!isStrEmpty(event.data.data.token)) {
          this.queryParams.account = event.data.data.token;
          this.$store.commit("SET_LOGIN", true);
        }
        if (!isStrEmpty(event.data.data.account)) {
          this.queryParams.token = event.data.data.account;
        }
        if (!isStrEmpty(event.data.data.customerId)) {
          this.queryParams.customerId = event.data.data.customerId;
        }

        this.$store.commit("SET_QUERY_PARAMS", this.queryParams);
      }
    },

    // observeDOMChanges() {
    //     const element = this.$refs.numbersPage;
    //     const observer = new MutationObserver((mutationsList, observer) => {
    //         for (const mutation of mutationsList) {
    //             if (mutation.type === 'childList' || mutation.type === 'attributes') {
    //                 this.updateElementHeight();
    //             }
    //         }
    //     });

    //     observer.observe(element, { childList: true, subtree: true, attributes: true });
    // },
    // updateElementHeight() {
    //     this.timeOutClear.forEach(element => {
    //         clearTimeout(element)
    //     });
    //     this.timeOutClear.push(setTimeout(() => {
    //         const element = this.$refs.numbersPage;
    //         const newHeight = element.scrollHeight
    //         if (newHeight > 0 && newHeight !== this.elementHeight) {
    //             let marginBottom = this.bottomValue < 80 ? 80 : this.bottomValue;
    //             this.elementHeight = newHeight + marginBottom;
    //             console.log("aaa", this.elementHeight)
    //             parent.postMessage({ command: "iframe-numbers-height", height: newHeight + marginBottom }, "*");
    //         }
    //     }, 100));
    // },
  },
};
</script>
<style lang="scss">
@import "@/public/scss/main.scss";
.app-container {
  display: flex;
  flex-direction: column;
  height: 100vh;
  /*overflow: hidden;*/
}

.pd-bottom {
  padding-bottom: 50px;
}

.content-wrapper {
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
  /*overflow: hidden;*/
}

.scrollable-content {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-color: #c1c1c1 #f1f1f1;
  scrollbar-width: auto;

  /* WebKit scrollbar styling */
  &::-webkit-scrollbar {
    width: 20px;
    height: 20px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.6);
    border-radius: 8px;
    border: 2px solid #f1f1f1;
  }

  &::-webkit-scrollbar-track {
    background-color: #151313;
    border-radius: 8px;
  }

  &::-webkit-scrollbar-button {
    display: block;
    width: 12px;
    height: 12px;
    background: rgba(0, 0, 0, 0.3);
  }

  &::-webkit-scrollbar-button:increment {
    background-image: url('data:image/svg+xml;utf8,<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path fill="%23FFFFFF" d="M12 4l-8 8h16z"/></svg>');
    background-repeat: no-repeat;
    background-position: center;
  }

  &::-webkit-scrollbar-button:decrement {
    background-image: url('data:image/svg+xml;utf8,<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path fill="%23FFFFFF" d="M12 20l8-8H4z"/></svg>');
    background-repeat: no-repeat;
    background-position: center;
  }
}

@media screen and (max-width: 768px) and (-webkit-min-device-pixel-ratio: 0) {
  .pd-bottom {
    padding-bottom: 22px; /* Mobile-only padding for Chrome/Safari */
  }
}
</style>
